import { useRoute, useSearchParams } from "wouter";
import {
  Sparkles,
  Lock,
  Unlock,
  MapPin,
  Filter,
  Grid,
  ArrowUpDown,
  CheckCircle2,
  Circle,
  MinusCircle,
  Plus,
  Users,
  UserCircle,
  Heart,
  Cake,
  Mail,
  RefreshCw,
  AlertTriangle,
  Flag,
  History,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { useAdminEventDetails, useAdminEventTables, useGenerateMatches, useToggleTableLock, useReassignBooking, useCreateTable, useUpdateTable, useDeleteTable, useAdminNeighborhoods, useAdminUserHistory, useAdminEvents, useToggleAssignmentReview } from "../api";
import { useUpdateAdminBooking } from "../api/useAdminUsers";
import { useOpenNoShowCase } from "../api/useAdminNoShowFees";
import {
  useSendTableEmails,
  useSendUserEmails,
  EmailTemplate,
  EmailMethod,
  UserEmailResponse,
} from "../api/useAdminEmails";
import { TableInfo } from "../components/TableSelector";
import { useToast } from "@/components/ux/Toast";
import { useConfirm } from "@/components/ConfirmModal";
import { FilterDropdown } from "../components/FilterDropdown";
import { StatsBar } from "../components/StatsBar";
import { TableModal } from "../components/TableModal";
import { TableCard } from "../components/TableCard";
import { UnassignedUsersArea } from "../components/UnassignedUsersArea";
import { EmailModal } from "../components/EmailModal";
import { TableManagementSection } from "../components/TableManagementSection";
import { AutoMatchModal } from "../components/AutoMatchModal";
import { EditReservationModal } from "../components/EditReservationModal";
import { ReassignmentDialog } from "../components/ReassignmentDialog";
import { TableReassignDialog } from "../components/TableReassignDialog";
import { UserEmailModal } from "../components/UserEmailModal";
import { calculateAge, formatArray } from "../utils/userUtils";
import { formatDateTimeToDisplay } from "@/utils/dateUtils";
import { AssignableUser } from "../components/DraggableUser";
import { ROLE_CONFIG } from "../components/roleConfig";
import { AdminAPI } from "@shared/types/api";
import { CloseOutBarConfig } from "../components/CloseOutBarConfig";
import { TableSearchInput } from "../components/TableSearchInput";
import { AuditLogModal } from "../components/AuditLogModal";
import OpsBanner from "../components/ops/OpsBanner";

interface Table {
  id: string;
  reservationName: string;
  capacity: number;
  isLocked: boolean;
  users: AssignableUser[];
  restaurantName?: string;
  restaurantId?: string;
  restaurantAddress?: string;
  restaurantNeighborhoodName?: string;
  restaurantBookingLink?: string;
  restaurantTags?: string[];
  reservationDone?: Date | string | null;
}

export default function AdminMatching() {
  const [, params] = useRoute("/matching/:eventId");
  const eventId = params?.eventId;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTableSearch = searchParams.get("q") || "";
  const [tableSearch, setTableSearch] = useState(urlTableSearch);

  useEffect(() => {
    setTableSearch(urlTableSearch);
  }, [urlTableSearch]);

  useEffect(() => {
    const current = searchParams.get("q") || "";
    if (tableSearch === current) return;
    const next = new URLSearchParams(searchParams);
    if (tableSearch) {
      next.set("q", tableSearch);
    } else {
      next.delete("q");
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSearch]);

  // Neighborhood filter is deep-linkable (?neighborhood=<name>) — the ops
  // dashboard links here pre-filtered to one neighborhood. Same URL-sync
  // pattern as the table search above.
  const urlNeighborhood = searchParams.get("neighborhood") || "all";

  const [tables, setTables] = useState<Table[]>([]);
  const [unassignedUsers, setUnassignedUsers] = useState<AssignableUser[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [userIdToBookingIdMap, setUserIdToBookingIdMap] = useState<
    Map<string, string>
  >(new Map());
  const [neighborhoodFilter, setNeighborhoodFilter] = useState<string>(urlNeighborhood);

  useEffect(() => {
    setNeighborhoodFilter(urlNeighborhood);
  }, [urlNeighborhood]);

  useEffect(() => {
    const current = searchParams.get("neighborhood") || "all";
    if (neighborhoodFilter === current) return;
    const next = new URLSearchParams(searchParams);
    if (neighborhoodFilter && neighborhoodFilter !== "all") {
      next.set("neighborhood", neighborhoodFilter);
    } else {
      next.delete("neighborhood");
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodFilter]);
  const [lockStatusFilter, setLockStatusFilter] = useState<
    "all" | "locked" | "unlocked"
  >("all");
  const [occupancyFilter, setOccupancyFilter] = useState<
    "all" | "empty" | "partial" | "full"
  >("all");
  const [reviewFilter, setReviewFilter] = useState<"all" | "flagged">("all");
  const [sortBy, setSortBy] = useState<
    "restaurant" | "table-number" | "capacity" | "occupancy"
  >("restaurant");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showTableModal, setShowTableModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailResults, setEmailResults] = useState<any>(null);
  const [showMatchDataDialog, setShowMatchDataDialog] = useState(false);
  const [matchData, setMatchData] = useState<string[]>([]);
  const [showAutoMatchModal, setShowAutoMatchModal] = useState(false);
  const [showEditReservationModal, setShowEditReservationModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassigningBookingId, setReassigningBookingId] = useState<string | null>(null);
  // Source table for the batch move/unassign-all dialog.
  const [reassignAllTableId, setReassignAllTableId] = useState<string | null>(null);
  const [showUserEmailModal, setShowUserEmailModal] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [emailingUserId, setEmailingUserId] = useState<string | null>(null);
  const [userEmailDryRunResult, setUserEmailDryRunResult] = useState<UserEmailResponse | null>(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: eventData, isLoading, isRefetching, refetch: refetchEvent } = useAdminEventDetails(eventId);
  const { data: tablesData, refetch: refetchTables } = useAdminEventTables(eventId);
  const { data: neighborhoodsData } = useAdminNeighborhoods();
  const { data: eventsData } = useAdminEvents();
  const updateBooking = useUpdateAdminBooking(eventId);

  const generateMatches = useGenerateMatches(eventId);
  const reassignBooking = useReassignBooking(eventId);
  const toggleLock = useToggleTableLock();
  const createTable = useCreateTable(eventId);
  const updateTable = useUpdateTable(eventId);
  const deleteTable = useDeleteTable(eventId);
  const sendTableEmails = useSendTableEmails();
  const sendUserEmails = useSendUserEmails();
  const toggleAssignmentReview = useToggleAssignmentReview();

  // Process API data into tables and unassigned users
  useEffect(() => {
    if (!eventData?.data) return;

    const { bookings, acceptedInvites, userNotes } = eventData.data as any;
    // Use tables from tablesData if available (more up-to-date), otherwise fall back to eventData
    const tablesResponse = tablesData?.data as { tables?: any[]; assignments?: any[] } | undefined;
    // Prefer tablesData if it exists and has complete data, otherwise use eventData
    // This ensures we don't mix stale data from different sources
    const availableTables = (tablesResponse?.tables && tablesResponse?.assignments)
      ? tablesResponse.tables 
      : eventData.data.tables;
    const assignments = (tablesResponse?.tables && tablesResponse?.assignments)
      ? tablesResponse.assignments
      : eventData.data.assignments;

    const userMap = new Map<string, AssignableUser>();
    const bookingIdToUserIdMap = new Map<string, string>();
    const userIdToBookingIdMapTemp = new Map<string, string>();
    bookings
      .filter(
        (booking: any) =>
          booking.status === "active" || booking.status === "cant_be_seated"
      )
      .forEach((booking: any) => {
        if (!booking.user) return;
        userMap.set(booking.userId, {
          id: booking.userId,
          name: `${booking.user.firstName} ${
            booking.user.lastName || ""
          }`.trim(),
          email: booking.user.email || "",
          role: booking.user.role || undefined,
          neighborhood: booking.neighborhood?.name || undefined,
          quizAnswers: JSON.parse(booking.user.quizAnswers),
          specialRequests: booking.specialRequests || undefined,
          confirmedAt: booking.confirmedAt,
          bookedAt: booking.createdAt,
          cantBeSeated: booking.status === "cant_be_seated",
          adminNotes: userNotes?.[booking.userId] || undefined,
        });
        bookingIdToUserIdMap.set(booking.id, booking.userId);
        userIdToBookingIdMapTemp.set(booking.userId, booking.id);
      });

    // Map each user to their accepted +1 partners (both directions).
    if (Array.isArray(acceptedInvites)) {
      const partnersByUser = new Map<
        string,
        Array<{ userId: string; name: string }>
      >();
      for (const inv of acceptedInvites) {
        if (!inv.inviterUserId || !inv.inviteeUserId) continue;
        const inviter = userMap.get(inv.inviterUserId);
        const invitee = userMap.get(inv.inviteeUserId);
        if (inviter) {
          const list = partnersByUser.get(inv.inviterUserId) || [];
          list.push({
            userId: inv.inviteeUserId,
            name: invitee?.name || "their +1",
          });
          partnersByUser.set(inv.inviterUserId, list);
        }
        if (invitee) {
          const list = partnersByUser.get(inv.inviteeUserId) || [];
          list.push({
            userId: inv.inviterUserId,
            name: inviter?.name || "their +1",
          });
          partnersByUser.set(inv.inviteeUserId, list);
        }
      }
      for (const [userId, partners] of partnersByUser.entries()) {
        const user = userMap.get(userId);
        if (user) user.plusOnePartners = partners;
      }
    }

    setUserIdToBookingIdMap(userIdToBookingIdMapTemp);

    const assignmentsByTable = new Map<string, any[]>();
    assignments.forEach((assignment: any) => {
      if (!assignmentsByTable.has(assignment.availableTableId)) {
        assignmentsByTable.set(assignment.availableTableId, []);
      }
      assignmentsByTable.get(assignment.availableTableId)!.push(assignment);
    });

    const processedTables: Table[] = availableTables.map((table: any) => {
      const tableAssignments = assignmentsByTable.get(table.id) || [];
      const users: AssignableUser[] = tableAssignments
        .map((assignment: any) => {
          const userId = bookingIdToUserIdMap.get(assignment.bookingId);
          return userId ? {...userMap.get(userId)!, assignmentReasoning: assignment.reasoning, assignmentId: assignment.id, flaggedForReview: assignment.flaggedForReview } : undefined;
        }).filter(a => a !== undefined);

      return {
        id: table.id,
        reservationName: table.reservationName,
        capacity: table.capacity,
        isLocked: table.state === "locked",
        restaurantName: table.restaurant?.name,
        restaurantId: table.restaurant?.id,
        restaurantAddress: table.restaurant?.address,
        restaurantNeighborhoodName: table.restaurant?.neighborhoodName,
        restaurantBookingLink: table.restaurant?.bookingLink,
        restaurantTags: table.restaurant?.tags,
        reservationDone: table.reservationDone,
        users,
      };
    });

    setTables(processedTables);

    const assignedBookingIds = new Set(
      assignments.map((a: any) => a.bookingId)
    );
    const assignedUserIds = new Set(
      Array.from(bookingIdToUserIdMap.entries())
        .filter(([bookingId]) => assignedBookingIds.has(bookingId))
        .map(([, userId]) => userId)
    );
    const unassigned = Array.from(userMap.values()).filter(
      (user) => !assignedUserIds.has(user.id)
    );
    setUnassignedUsers(unassigned);
  }, [eventData, tablesData]);

  const allUsers = [...unassignedUsers, ...tables.flatMap((t) => t.users)];
  const allUserIds = allUsers.map((u) => u.id);
  const { data: userHistory } = useAdminUserHistory(allUserIds, eventId);
  const availableNeighborhoods = Array.from(
    new Set(
      allUsers.map((u) => u.neighborhood).filter((n): n is string => Boolean(n))
    )
  );

  // Create a map from neighborhood name to city name for display
  const neighborhoodToCityMap = new Map<string, string>();
  neighborhoodsData?.data?.neighborhoods?.forEach((n: any) => {
    if (n.cityName) {
      neighborhoodToCityMap.set(n.name.toLowerCase(), n.cityName);
    }
  });

  // Filter and sort tables
  let filteredTables = tables;

  if (neighborhoodFilter !== "all") {
    const targetNeighborhood = neighborhoodFilter.toLowerCase();
    // Keep tables in the filtered neighborhood (matches restaurant's neighborhood
    // case-insensitively) — empty tables in the neighborhood are included so the
    // admin can drop users into them.
    filteredTables = filteredTables.filter(
      (table) =>
        table.restaurantNeighborhoodName?.toLowerCase() === targetNeighborhood
    );
    // For each kept table, only surface users from that neighborhood so the
    // counts on the card reflect the active filter.
    filteredTables = filteredTables.map((table) => ({
      ...table,
      users: table.users.filter(
        (u) => u.neighborhood?.toLowerCase() === targetNeighborhood
      ),
    }));
  }

  if (lockStatusFilter !== "all") {
    filteredTables = filteredTables.filter((table) =>
      lockStatusFilter === "locked" ? table.isLocked : !table.isLocked
    );
  }

  if (occupancyFilter !== "all") {
    filteredTables = filteredTables.filter((table) => {
      const occupancy = table.users.length;
      const capacity = table.capacity;

      if (occupancyFilter === "empty") return occupancy === 0;
      if (occupancyFilter === "full") return occupancy >= capacity;
      if (occupancyFilter === "partial")
        return occupancy > 0 && occupancy < capacity;
      return true;
    });
  }

  if (reviewFilter === "flagged") {
    filteredTables = filteredTables.filter((table) =>
      table.users.some((u) => u.flaggedForReview)
    );
  }

  const tableSearchHaystacks = useMemo(() => {
    const start = Date.now();
    const map = new Map<string, string>();
    tables.forEach((table) => {
      const tableCity = table.restaurantNeighborhoodName
        ? neighborhoodToCityMap.get(table.restaurantNeighborhoodName.toLowerCase())
        : undefined;
      const userFields = table.users.flatMap((u) => [
        u.name,
        u.email,
        u.neighborhood,
        u.neighborhood
          ? neighborhoodToCityMap.get(u.neighborhood.toLowerCase())
          : undefined,
      ]);
      const haystack = [
        table.reservationName,
        table.id,
        table.restaurantName,
        table.restaurantId,
        table.restaurantNeighborhoodName,
        tableCity,
        table.restaurantAddress,
        ...(table.restaurantTags || []),
        ...userFields,
      ]
        .filter((v): v is string => Boolean(v))
        .join("\n")
        .toLowerCase();
      map.set(table.id, haystack);
    });
    console.log('Computation took', Date.now() - start);
    return map;
    // neighborhoodToCityMap is rebuilt on every render; depend on its data source instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, neighborhoodsData]);

  const normalizedTableSearch = tableSearch.trim().toLowerCase();
  if (normalizedTableSearch) {
    const start = Date.now();
    filteredTables = filteredTables.filter((table) =>
      (tableSearchHaystacks.get(table.id) || "").includes(normalizedTableSearch)
    );
    console.log('Filtering took', Date.now() - start);
  }

  filteredTables = [...filteredTables].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case "restaurant":
        comparison = (a.restaurantName || "").localeCompare(
          b.restaurantName || ""
        );
        if (comparison === 0) {
          comparison = (a.reservationName || "").localeCompare(
            b.reservationName || "",
            undefined,
            { numeric: true }
          );
        }
        break;
      case "table-number":
        comparison = (a.reservationName || "").localeCompare(
          b.reservationName || "",
          undefined,
          { numeric: true }
        );
        break;
      case "capacity":
        comparison = a.capacity - b.capacity;
        break;
      case "occupancy":
        comparison = a.users.length - b.users.length;
        break;
    }

    return sortOrder === "asc" ? comparison : -comparison;
  });

  const filteredUnassignedUsers = (
    neighborhoodFilter === "all"
      ? [...unassignedUsers]
      : // Case-insensitive like the table filter — the ops dashboard deep-links
        // here with canonical neighborhood names that may differ in casing.
        unassignedUsers.filter(
          (u) => u.neighborhood?.toLowerCase() === neighborhoodFilter.toLowerCase()
        )
  ).sort((a, b) => {
    const cantSeatedDiff = Number(!!a.cantBeSeated) - Number(!!b.cantBeSeated);
    if (cantSeatedDiff !== 0) return cantSeatedDiff;
    return (a.neighborhood || "").localeCompare(b.neighborhood || "");
  });

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id as string | null);
  };

  // Sends either the BOOKING_DETAILS ("you're assigned, here's where to go")
  // or BOOKING_UPDATED ("your table changed") email after the admin moves
  // a user onto a locked table. Caller decides which based on whether the
  // user was previously on a locked table.
  const promptSendPostMoveEmail = async (
    userId: string,
    userName: string,
    previouslyOnLockedTable: boolean
  ) => {
    if (!eventId) return;
    const template = previouslyOnLockedTable
      ? EmailTemplate.BOOKING_UPDATED
      : EmailTemplate.BOOKING_DETAILS;
    const title = previouslyOnLockedTable
      ? "Send updated booking email?"
      : "Send assignment email?";
    const message = previouslyOnLockedTable
      ? `${userName} already had a locked table. Let them know their table changed?`
      : `${userName} was added to a locked table. Send the booking details email so they know where to go?`;
    const successLabel = previouslyOnLockedTable
      ? "Updated booking email"
      : "Assignment email";

    const confirmed = await confirm({
      title,
      message,
      confirmText: "Send email",
      cancelText: "Skip",
    });
    if (!confirmed) return;

    sendUserEmails.mutate(
      {
        eventId,
        userIds: [userId],
        template,
        dryRun: false,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success(`${successLabel} sent to ${userName}`);
          } else {
            toast.error(data.error || `Failed to send ${successLabel.toLowerCase()}`);
          }
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to send assignment email");
        },
      }
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const activeUserId = active.id as string;
    const overId = over.id as string;

    const sourceTable = tables.find((t) =>
      t.users.some((u) => u.id === activeUserId)
    );
    const isFromUnassigned =
      !sourceTable && unassignedUsers.some((u) => u.id === activeUserId);

    const bookingId = userIdToBookingIdMap.get(activeUserId);

    // Drop onto unassigned area or onto an unassigned user → unassign.
    const isDropOnUnassignedUser = unassignedUsers.some((u) => u.id === overId);
    if (
      (overId === "unassigned-area" || isDropOnUnassignedUser) &&
      sourceTable
    ) {
      const user = sourceTable.users.find((u) => u.id === activeUserId);
      if (!user || !bookingId) return;

      setTables((prev) =>
        prev.map((table) =>
          table.id === sourceTable.id
            ? {
                ...table,
                users: table.users.filter((u) => u.id !== activeUserId),
              }
            : table
        )
      );
      setUnassignedUsers((prev) => [...prev, user]);

      reassignBooking.mutate(
        { bookingId, action: "unassign" },
        {
          onError: () => {
            setTables((prev) =>
              prev.map((table) =>
                table.id === sourceTable.id
                  ? { ...table, users: [...table.users, user] }
                  : table
              )
            );
            setUnassignedUsers((prev) =>
              prev.filter((u) => u.id !== activeUserId)
            );
          },
        }
      );
      return;
    }

    let targetTableId: string | null = null;
    if (overId.startsWith("table-")) {
      targetTableId = overId.replace("table-", "");
    } else {
      const targetTable = tables.find((t) =>
        t.users.some((u) => u.id === overId)
      );
      if (targetTable) targetTableId = targetTable.id;
    }
    if (!targetTableId) return;

    const targetTable = tables.find((t) => t.id === targetTableId);
    if (!targetTable) return;

    // Drop into a table → assign / move via unified endpoint.
    if (isFromUnassigned || (sourceTable && sourceTable.id !== targetTableId)) {
      const user = isFromUnassigned
        ? unassignedUsers.find((u) => u.id === activeUserId)
        : sourceTable!.users.find((u) => u.id === activeUserId);
      if (!user || !bookingId) return;

      // Optimistic UI: remove from source, add to target.
      if (isFromUnassigned) {
        setUnassignedUsers((prev) => prev.filter((u) => u.id !== activeUserId));
      }
      setTables((prev) =>
        prev.map((table) => {
          if (sourceTable && table.id === sourceTable.id) {
            return {
              ...table,
              users: table.users.filter((u) => u.id !== activeUserId),
            };
          }
          if (table.id === targetTableId) {
            return { ...table, users: [...table.users, user] };
          }
          return table;
        })
      );

      const prevLocked = !!sourceTable?.isLocked;
      reassignBooking.mutate(
        { bookingId, action: "assign", tableId: targetTableId },
        {
          onSuccess: () => {
            if (targetTable.isLocked) {
              promptSendPostMoveEmail(user.id, user.name, prevLocked);
            }
          },
          onError: () => {
            // Revert optimistic move.
            setTables((prev) =>
              prev.map((table) => {
                if (sourceTable && table.id === sourceTable.id) {
                  return { ...table, users: [...table.users, user] };
                }
                if (table.id === targetTableId) {
                  return {
                    ...table,
                    users: table.users.filter((u) => u.id !== activeUserId),
                  };
                }
                return table;
              })
            );
            if (isFromUnassigned) {
              setUnassignedUsers((prev) => [...prev, user]);
            }
          },
        }
      );
      return;
    }

    // Reorder within the same table (UI-only).
    if (
      sourceTable &&
      sourceTable.id === targetTableId &&
      !overId.startsWith("table-")
    ) {
      const oldIndex = sourceTable.users.findIndex((u) => u.id === activeUserId);
      const newIndex = sourceTable.users.findIndex((u) => u.id === overId);
      if (oldIndex !== newIndex) {
        setTables((prev) =>
          prev.map((table) =>
            table.id === sourceTable.id
              ? { ...table, users: arrayMove(table.users, oldIndex, newIndex) }
              : table
          )
        );
      }
    }
  };

  const handleOpenAutoMatchModal = () => {
    setShowAutoMatchModal(true);
  };

  const handleGenerateMatches = async (systemInstructions: string) => {
    generateMatches.mutate({ systemInstructions }, {
      onSuccess: (response: any) => {
        setShowAutoMatchModal(false);
        if (response?.data?.matchData) {
          setMatchData(response.data.matchData);
          setShowMatchDataDialog(true);
        } else {
          toast.success("Matches generated successfully");
        }
      },
      onError: (error: any) => {
        const errorMessage =
          error?.message ||
          "Failed to generate matches. Please ensure there is at least one restaurant in the system.";
        toast.error(errorMessage);
      },
    });
  };

  const handleToggleLock = (tableId: string) => {
    const table = tables.find((t) => t.id === tableId);
    if (table) {
      const newLockState = !table.isLocked;

      setTables((prev) =>
        prev.map((t) =>
          t.id === tableId ? { ...t, isLocked: newLockState } : t
        )
      );

      toggleLock.mutate(
        { tableId, isActive: !newLockState },
        {
          onError: () => {
            setTables((prev) =>
              prev.map((t) =>
                t.id === tableId ? { ...t, isLocked: !newLockState } : t
              )
            );
          },
        }
      );
    }
  };

  const handleCloseAllAssignedTables = async () => {
    // Find all tables that have users assigned and are not already locked
    const tablesToClose = tables.filter((t) => t.users.length > 0 && !t.isLocked);

    if (tablesToClose.length === 0) {
      toast.error("No unlocked tables with assigned users to close");
      return;
    }

    const confirmed = await confirm({
      title: "Close Assigned Tables",
      message: `Close ${tablesToClose.length} table(s) with assigned users? This will lock them.`,
      confirmText: "Close tables",
      cancelText: "Cancel",
    });
    if (!confirmed) return;

    // Optimistically update UI
    setTables((prev) =>
      prev.map((t) =>
        t.users.length > 0 ? { ...t, isLocked: true } : t
      )
    );

    // Make API calls for each table
    let successCount = 0;
    let errorCount = 0;

    tablesToClose.forEach((table) => {
      toggleLock.mutate(
        { tableId: table.id, isActive: false }, // isActive: false means locked
        {
          onSuccess: () => {
            successCount++;
            if (successCount + errorCount === tablesToClose.length) {
              if (errorCount === 0) {
                toast.success(`Closed ${successCount} table(s) successfully`);
              } else {
                toast.error(`Closed ${successCount} table(s), but ${errorCount} failed`);
              }
            }
          },
          onError: () => {
            errorCount++;
            // Revert the specific table on error
            setTables((prev) =>
              prev.map((t) =>
                t.id === table.id ? { ...t, isLocked: false } : t
              )
            );
            if (successCount + errorCount === tablesToClose.length) {
              toast.error(`Closed ${successCount} table(s), but ${errorCount} failed`);
            }
          },
        }
      );
    });
  };

  const handleDeleteTable = async (tableId: string) => {
    const table = tables.find((t) => t.id === tableId);
    if (!table) return;

    if (table.users.length > 0) {
      toast.error(
        "Cannot delete table with assigned users. Please unassign all users first."
      );
      return;
    }

    const confirmed = await confirm({
      title: "Delete Table",
      message: `Are you sure you want to delete "${table.reservationName}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (confirmed) {
      deleteTable.mutate(tableId, {
        onSuccess: () => {
          toast.success("Table deleted successfully");
          setShowTableModal(false);
          setEditingTableId(null);
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to delete table");
        },
      });
    }
  };

  const handleEditTable = (tableId: string) => {
    setEditingTableId(tableId);
    setModalMode("edit");
    setShowTableModal(true);
  };

  const handleAddTable = () => {
    setEditingTableId(null);
    setModalMode("add");
    setShowTableModal(true);
  };

  const handleOpenEmailModal = () => {
    setEmailResults(null);
    setShowEmailModal(true);
  };

  const handleEditUser = (userId: string) => {
    setEditingUserId(userId);
    setShowEditReservationModal(true);
  };

  // Flag a seated guest as a no-show: opens a case that follows the same
  // notify → grace → charge path as peer-reported ones (docs/NO_SHOW_FEES.md).
  // Only offered once the dinner date has passed; before that it's meaningless.
  const openNoShowCase = useOpenNoShowCase();
  const dinnerHasHappened =
    !!eventData?.data?.event && eventData.data.event.date <= new Date().toISOString().slice(0, 10);
  const handleFlagNoShow = async (userId: string) => {
    const user = tables.flatMap((t) => t.users).find((u) => u.id === userId);
    const bookingId = userIdToBookingIdMap.get(userId);
    if (!user || !bookingId) return;
    const ok = await confirm({
      title: `Flag ${user.name} as a no-show?`,
      message:
        "This opens a no-show case. Depending on the current mode they may be emailed and, after the grace period, charged the no-show fee. You can excuse the case any time before that on the No-shows page.",
      confirmText: "Flag as no-show",
      type: "danger",
    });
    if (!ok) return;
    openNoShowCase.mutate(bookingId, {
      onSuccess: () => toast.success(`Opened a no-show case for ${user.name}`),
      onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to open no-show case"),
    });
  };

  const handleToggleReview = (userId: string) => {
    const user = tables.flatMap(t => t.users).find(u => u.id === userId);
    if (!user?.assignmentId) return;

    const newFlag = !user.flaggedForReview;

    setTables(prev => prev.map(t => ({
      ...t,
      users: t.users.map(u => u.id === userId ? { ...u, flaggedForReview: newFlag } : u),
    })));

    toggleAssignmentReview.mutate(
      { assignmentId: user.assignmentId, flaggedForReview: newFlag },
      {
        onError: () => {
          setTables(prev => prev.map(t => ({
            ...t,
            users: t.users.map(u => u.id === userId ? { ...u, flaggedForReview: !newFlag } : u),
          })));
        },
      }
    );
  };

  const handleReassign = (userId: string) => {
    const bookingId = userIdToBookingIdMap.get(userId);
    if (!bookingId) {
      toast.error("Booking not found for this user");
      return;
    }
    setReassigningBookingId(bookingId);
    setShowReassignModal(true);
  };

  const handleOpenUserEmail = (userId: string) => {
    setEmailingUserId(userId);
    setUserEmailDryRunResult(null);
    setShowUserEmailModal(true);
  };

  const handleUserEmailSend = (
    template: EmailTemplate,
    dryRun: boolean,
    customBroadcastId?: string
  ) => {
    if (!eventId || !emailingUserId) return;

    sendUserEmails.mutate(
      {
        eventId,
        userIds: [emailingUserId],
        template,
        dryRun,
        customBroadcastId,
      },
      {
        onSuccess: (data) => {
          if (dryRun) {
            setUserEmailDryRunResult(data);
          } else {
            if (data.success) {
              toast.success(
                `Email sent to ${data.recipients.length} user(s)`
              );
            } else {
              toast.error(data.error || "Failed to send email");
            }
            setShowUserEmailModal(false);
            setEmailingUserId(null);
            setUserEmailDryRunResult(null);
          }
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to send email");
        },
      }
    );
  };

  const handleReassignSuccess = () => {
    refetchEvent();
    refetchTables();
  };

  const handleEditReservationSubmit = (data: {
    eventTimeId?: string;
    neighborhoodId?: string;
  }) => {
    const bookingId = userIdToBookingIdMap.get(editingUserId!);
    if (!bookingId) {
      toast.error("Booking not found for this user");
      return;
    }
    if (Object.keys(data).length === 0) {
      setShowEditReservationModal(false);
      setEditingUserId(null);
      return;
    }

    updateBooking.mutate(
      { bookingId, data },
      {
        onSuccess: () => {
          toast.success("Reservation updated successfully");
          setShowEditReservationModal(false);
          setEditingUserId(null);
          refetchEvent();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to update reservation");
        },
      }
    );
  };

  const handleEmailSend = async (
    template: EmailTemplate,
    _method: EmailMethod,
    tableIds: string[],
    dryRun: boolean,
    customBroadcastId?: string
  ) => {
    if (!eventId) return;

    sendTableEmails.mutate(
      { eventId, tableIds, template, dryRun, customBroadcastId },
      {
        onSuccess: (data: any) => {
          if (dryRun) {
            setEmailResults(data);
          } else {
            toast.success(`Emails sent to ${data.results?.length || 0} table(s)!`);
            setShowEmailModal(false);
            setEmailResults(null);
          }
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to send emails");
        },
      }
    );
  };

  const handleModalSubmit = (data: {
    restaurantId: string;
    capacity: number;
    reservationName: string;
  }) => {
    if (modalMode === "add") {
      if (!eventId) return;

      createTable.mutate(
        {
          eventTimeId: eventId,
          restaurantId: data.restaurantId,
          capacity: data.capacity,
          reservationName: data.reservationName || undefined,
        },
        {
          onSuccess: () => {
            toast.success("Table created successfully");
            setShowTableModal(false);
          },
          onError: (error: any) => {
            toast.error(error?.message || "Failed to create table");
          },
        }
      );
    } else if (modalMode === "edit" && editingTableId) {
      const table = tables.find((t) => t.id === editingTableId);
      if (!table) return;

      const updateData: any = {};
      let hasChanges = false;

      if (data.reservationName !== table.reservationName) {
        updateData.reservationName = data.reservationName;
        hasChanges = true;
      }

      if (data.capacity !== table.capacity) {
        updateData.capacity = data.capacity;
        hasChanges = true;
      }

      if (data.restaurantId !== table.restaurantId) {
        updateData.restaurantId = data.restaurantId;
        hasChanges = true;
      }

      if (!hasChanges) {
        setShowTableModal(false);
        setEditingTableId(null);
        return;
      }

      // Find the new restaurant info for optimistic update
      const newRestaurant = allRestaurants.find((r: any) => r.id === data.restaurantId);

      setTables((prev) =>
        prev.map((t) =>
          t.id === editingTableId
            ? {
                ...t,
                reservationName: data.reservationName,
                capacity: data.capacity,
                restaurantId: data.restaurantId,
                restaurantName: newRestaurant?.name,
                restaurantAddress: newRestaurant?.address,
                restaurantNeighborhoodName: newRestaurant?.neighborhoodName,
                restaurantBookingLink: newRestaurant?.bookingLink,
              }
            : t
        )
      );

      updateTable.mutate(
        { tableId: editingTableId, data: updateData },
        {
          onSuccess: () => {
            toast.success("Table updated successfully");
            setShowTableModal(false);
            setEditingTableId(null);
          },
          onError: (error: any) => {
            setTables((prev) =>
              prev.map((t) =>
                t.id === editingTableId
                  ? {
                      ...t,
                      reservationName: table.reservationName,
                      capacity: table.capacity,
                      restaurantId: table.restaurantId,
                      restaurantName: table.restaurantName,
                      restaurantAddress: table.restaurantAddress,
                      restaurantNeighborhoodName: table.restaurantNeighborhoodName,
                      restaurantBookingLink: table.restaurantBookingLink,
                    }
                  : t
              )
            );
            toast.error(error?.message || "Failed to update table");
          },
        }
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <div className="loading loading-spinner loading-lg text-primary"></div>
      </div>
    );
  }

  const event = eventData?.data?.event;
  const bookingsCount =
    eventData?.data?.bookings?.length || 0;

  // Get all available restaurants
  const allRestaurants = (() => {
    const existingRestaurants = eventData?.data?.tables
      ? Array.from(
          new Map(
            eventData.data.tables
              .filter((t: any) => t.restaurant)
              .map((t: any) => [t.restaurant.id, t.restaurant])
          ).values()
        )
      : [];

    const neighborhoodRestaurants = neighborhoodsData?.data?.restaurants || [];

    const restaurantMap = new Map();
    [...existingRestaurants, ...neighborhoodRestaurants].forEach((r: any) => {
      if (r.isActive !== false) {
        restaurantMap.set(r.id, r);
      }
    });

    return Array.from(restaurantMap.values());
  })();

  const editingTable = editingTableId
    ? tables.find((t) => t.id === editingTableId)
    : null;

  // Find tables with neighborhood mismatches (users assigned to restaurants in different neighborhoods)
  const neighborhoodMismatches = tables
    .filter((table) => table.restaurantNeighborhoodName)
    .flatMap((table) =>
      table.users
        .filter(
          (user) =>
            user.neighborhood &&
            user.neighborhood.toLowerCase() !==
              table.restaurantNeighborhoodName?.toLowerCase()
        )
        .map((user) => ({
          tableId: table.id,
          tableName: table.reservationName,
          restaurantName: table.restaurantName,
          restaurantNeighborhood: table.restaurantNeighborhoodName,
          userName: user.name,
          userNeighborhood: user.neighborhood,
        }))
    );

  // Find accepted "+1" pairs that ended up at different tables (or with one
  // partner unassigned). Computed across the full `tables` set, not filtered,
  // so the count is stable even as the admin filters the view.
  const userIdToTableId = new Map<string, string>();
  for (const t of tables) {
    for (const u of t.users) userIdToTableId.set(u.id, t.id);
  }
  // Dedupe pairs by ordering the two ids — each split should appear once.
  const seenPairs = new Set<string>();
  const plusOneSplits: Array<{
    userName: string;
    partnerName: string;
    sameUnassigned: boolean;
  }> = [];
  for (const t of tables) {
    for (const u of t.users) {
      for (const p of u.plusOnePartners || []) {
        const key = [u.id, p.userId].sort().join("|");
        if (seenPairs.has(key)) continue;
        const partnerTableId = userIdToTableId.get(p.userId);
        if (partnerTableId !== t.id) {
          seenPairs.add(key);
          plusOneSplits.push({
            userName: u.name,
            partnerName: p.name,
            sameUnassigned: !partnerTableId,
          });
        }
      }
    }
  }
  // Catch the case where neither partner is on a table.
  for (const u of unassignedUsers) {
    for (const p of u.plusOnePartners || []) {
      const key = [u.id, p.userId].sort().join("|");
      if (seenPairs.has(key)) continue;
      const partnerTableId = userIdToTableId.get(p.userId);
      if (!partnerTableId) {
        seenPairs.add(key);
        plusOneSplits.push({
          userName: u.name,
          partnerName: p.name,
          sameUnassigned: true,
        });
      }
    }
  }

  // Staff/test accounts (users with a role) that are seated at a real table —
  // usually unintended, so surface them for the admin to double-check.
  const roleAssignedToTables: Array<{ name: string; role: AdminAPI.UserRole }> = [];
  for (const t of tables) {
    for (const u of t.users) {
      if (u.role) roleAssignedToTables.push({ name: u.name, role: u.role });
    }
  }

  // Convert tables to TableInfo format for the selector
  const tableInfoList: TableInfo[] = tables.map((table) => ({
    id: table.id,
    name: table.reservationName,
    isClosed: table.isLocked,
    userCount: table.users.length,
    capacity: table.capacity,
    restaurantName: table.restaurantName,
    neighborhood: table.restaurantNeighborhoodName,
  }));

  if (unassignedUsers.length > 0) {
    tableInfoList.unshift({
      id: "unassigned-users",
      name: "Unassigned Users",
      isClosed: false,
      userCount: unassignedUsers.length,
      capacity: unassignedUsers.length,
      restaurantName: "All Unassigned",
    });
  }

  return (
    <div className="px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className="mb-8 flex items-center justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold mb-2">Table Matching</h1>
            <p className="text-base-content/60">
              {event?.date
                ? formatDateTimeToDisplay(event.date, event.time)
                : "Loading..."}
            </p>
          </div>
        </div>

        {/* Neighborhood Mismatch Warning */}
        {neighborhoodMismatches.length > 0 && (
          <div
            className="mb-6 bg-amber-900 border border-amber-600 rounded-lg p-4"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
              <p className="text-amber-100">
                <span className="font-semibold">{neighborhoodMismatches.length} user(s)</span>{" "}
                assigned to restaurants in different neighborhoods than their selection.
              </p>
            </div>
          </div>
        )}

        {/* +1 split warning — accepted invites where partners ended up apart. */}
        {plusOneSplits.length > 0 && (
          <div
            className="mb-6 bg-amber-900 border border-amber-600 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-amber-100 text-sm">
                <p>
                  <span className="font-semibold">
                    {plusOneSplits.length} +1 pair(s)
                  </span>{" "}
                  not seated at the same table.
                </p>
                <ul className="mt-1.5 list-disc list-inside opacity-90">
                  {plusOneSplits.slice(0, 5).map((s, i) => (
                    <li key={i}>
                      {s.userName} ↔ {s.partnerName}
                      {s.sameUnassigned ? " (both unassigned)" : ""}
                    </li>
                  ))}
                  {plusOneSplits.length > 5 && (
                    <li>+{plusOneSplits.length - 5} more…</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Staff/test account warning — users with a role seated at a table. */}
        {roleAssignedToTables.length > 0 && (
          <div
            className="mb-6 bg-amber-900 border border-amber-600 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-amber-100 text-sm">
                <p>
                  <span className="font-semibold">
                    {roleAssignedToTables.length} staff/test account(s)
                  </span>{" "}
                  with a role are assigned to a table. Verify this is intended.
                </p>
                <ul className="mt-1.5 list-disc list-inside opacity-90">
                  {roleAssignedToTables.slice(0, 5).map((u, i) => (
                    <li key={i}>
                      {u.name} — {ROLE_CONFIG[u.role].label}
                    </li>
                  ))}
                  {roleAssignedToTables.length > 5 && (
                    <li>+{roleAssignedToTables.length - 5} more…</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* AI event-ops activity for this event */}
        {eventId && <OpsBanner eventTimeId={eventId} />}

        {/* Actions */}
        <div
          className="card mb-8"
        >
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <StatsBar
              totalBookings={bookingsCount}
              unassignedCount={filteredUnassignedUsers.length}
              tablesCount={filteredTables.length}
            />

            <div className="flex gap-2 items-center flex-wrap">
              <button onClick={handleAddTable} className="btn btn-secondary">
                <Plus className="w-4 h-4 mr-2" />
                Add Table
              </button>
              <button
                onClick={handleOpenEmailModal}
                disabled={tables.length === 0}
                className="btn btn-secondary"
                title={tables.length === 0 ? "No tables available" : "Send emails to selected tables"}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email users
              </button>
              <button
                onClick={handleCloseAllAssignedTables}
                disabled={tables.filter((t) => t.users.length > 0 && !t.isLocked).length === 0}
                className="btn btn-secondary"
                title="Close all tables that have people assigned"
              >
                <Lock className="w-4 h-4 mr-2" />
                Close Assigned Tables
              </button>
              <button
                onClick={handleOpenAutoMatchModal}
                disabled={generateMatches.isPending || bookingsCount === 0}
                className="btn btn-primary"
                title={bookingsCount === 0 ? "No bookings to match" : ""}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {generateMatches.isPending
                  ? "Generating..."
                  : "Auto-Generate Matches"}
              </button>
              <button
                onClick={() => setShowAuditLogModal(true)}
                disabled={!eventId}
                className="btn btn-secondary"
                title="View audit log for this event"
              >
                <History className="w-4 h-4 mr-2" />
                Audit log
              </button>
              <button
                onClick={() => {
                  refetchEvent();
                  refetchTables();
                }}
                className="btn btn-ghost btn-sm gap-2"
                title="Refresh data"
                disabled={isRefetching}
              >
                <RefreshCw className={`w-4 h-4 ${isRefetching ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 pt-4 border-t border-base-300 flex-wrap">
              {availableNeighborhoods.length > 0 && (
                <FilterDropdown
                  label="Neighborhood"
                  icon={<Filter className="w-3.5 h-3.5" />}
                  value={neighborhoodFilter}
                  options={[
                    { value: "all", label: "All Neighborhoods" },
                    ...[...availableNeighborhoods]
                      .sort((a, b) => {
                        const cityA = neighborhoodToCityMap.get(a.toLowerCase()) || "";
                        const cityB = neighborhoodToCityMap.get(b.toLowerCase()) || "";
                        // Sort by city first, then by neighborhood name
                        const cityCompare = cityA.localeCompare(cityB);
                        if (cityCompare !== 0) return cityCompare;
                        return a.localeCompare(b);
                      })
                      .map((n) => {
                        const cityName = neighborhoodToCityMap.get(n.toLowerCase());
                        const neighborhoodLabel = n.charAt(0).toUpperCase() + n.slice(1);
                        return {
                          value: n,
                          label: cityName ? `${cityName} - ${neighborhoodLabel}` : neighborhoodLabel,
                        };
                      }),
                  ]}
                  onChange={setNeighborhoodFilter}
                />
              )}

              <FilterDropdown
                label="Status"
                icon={
                  lockStatusFilter === "locked" ? (
                    <Lock className="w-3.5 h-3.5" />
                  ) : (
                    <Unlock className="w-3.5 h-3.5" />
                  )
                }
                value={lockStatusFilter}
                options={[
                  { value: "all", label: "All Tables" },
                  {
                    value: "unlocked",
                    label: "Unlocked",
                    icon: <Unlock className="w-3 h-3" />,
                  },
                  {
                    value: "locked",
                    label: "Locked",
                    icon: <Lock className="w-3 h-3" />,
                  },
                ]}
                onChange={(value) =>
                  setLockStatusFilter(value as typeof lockStatusFilter)
                }
              />

              <FilterDropdown
                label="Occupancy"
                icon={<Grid className="w-3.5 h-3.5" />}
                value={occupancyFilter}
                options={[
                  { value: "all", label: "All" },
                  {
                    value: "empty",
                    label: "Empty",
                    icon: <Circle className="w-3 h-3" />,
                  },
                  {
                    value: "partial",
                    label: "Partial",
                    icon: <MinusCircle className="w-3 h-3" />,
                  },
                  {
                    value: "full",
                    label: "Full",
                    icon: <CheckCircle2 className="w-3 h-3" />,
                  },
                ]}
                onChange={(value) =>
                  setOccupancyFilter(value as typeof occupancyFilter)
                }
              />

              <FilterDropdown
                label="Review"
                icon={<Flag className="w-3.5 h-3.5" />}
                value={reviewFilter}
                options={[
                  { value: "all", label: "All" },
                  {
                    value: "flagged",
                    label: "Needs Review",
                    icon: <Flag className="w-3 h-3" />,
                  },
                ]}
                onChange={(value) =>
                  setReviewFilter(value as typeof reviewFilter)
                }
              />

              <div className="dropdown">
                <label
                  tabIndex={0}
                  className="btn btn-sm btn-ghost normal-case gap-2 border border-base-300"
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  <span>
                    {sortBy === "restaurant"
                      ? "Restaurant"
                      : sortBy === "table-number"
                      ? "Table #"
                      : sortBy === "capacity"
                      ? "Capacity"
                      : "Occupancy"}
                  </span>
                  <span className="text-xs">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                </label>
                <ul
                  tabIndex={0}
                  className="dropdown-content z-[1] menu flex-nowrap p-2 shadow-xl bg-base-100 rounded-lg w-48 mt-1 border border-base-300 max-h-[60vh] overflow-y-auto"
                >
                  <li>
                    <a
                      onClick={() => setSortBy("restaurant")}
                      className={`text-sm ${
                        sortBy === "restaurant" ? "active" : ""
                      }`}
                    >
                      Restaurant
                    </a>
                  </li>
                  <li>
                    <a
                      onClick={() => setSortBy("table-number")}
                      className={`text-sm ${
                        sortBy === "table-number" ? "active" : ""
                      }`}
                    >
                      Table Number
                    </a>
                  </li>
                  <li>
                    <a
                      onClick={() => setSortBy("capacity")}
                      className={`text-sm ${
                        sortBy === "capacity" ? "active" : ""
                      }`}
                    >
                      Capacity
                    </a>
                  </li>
                  <li>
                    <a
                      onClick={() => setSortBy("occupancy")}
                      className={`text-sm ${
                        sortBy === "occupancy" ? "active" : ""
                      }`}
                    >
                      Occupancy
                    </a>
                  </li>
                  <li className="border-t border-base-300 mt-1 pt-1">
                    <a
                      onClick={() =>
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                      }
                      className="text-sm"
                    >
                      {sortOrder === "asc" ? "↑ Ascending" : "↓ Descending"}
                    </a>
                  </li>
                </ul>
              </div>
              <TableSearchInput
                value={tableSearch}
                onDebouncedChange={setTableSearch}
              />
            </div>
        </div>

        {/* Close-Out Bar Config */}
        <CloseOutBarConfig eventTimeId={eventId} />

        {/* Table Management Section */}
        <TableManagementSection
          tables={tables}
          allRestaurants={allRestaurants}
          eventId={eventId || ""}
          eventDate={event?.date}
          eventTime={event?.time}
          onRefresh={() => {
            refetchEvent();
            refetchTables();
          }}
          onToggleLock={handleToggleLock}
          unassignedUsers={unassignedUsers}
          availableNeighborhoods={availableNeighborhoods}
          neighborhoodToCityMap={neighborhoodToCityMap}
          userHistory={userHistory as any}
        />

        <DndContext
          sensors={sensors}
          collisionDetection={(args) => {
            const pointerCollisions = pointerWithin(args);
            if (pointerCollisions.length > 0) {
              return pointerCollisions;
            }

            const intersectionCollisions = rectIntersection(args);
            if (intersectionCollisions.length > 0) {
              return intersectionCollisions;
            }

            return closestCorners(args);
          }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Unassigned Users */}
            <div
              className="lg:col-span-1"
            >
              <UnassignedUsersArea
                users={filteredUnassignedUsers}
                activeUserId={activeId}
                overElementId={overId}
                allTables={tables}
                neighborhoodFilter={neighborhoodFilter}
                availableNeighborhoods={availableNeighborhoods}
                neighborhoodToCityMap={neighborhoodToCityMap}
                onNeighborhoodFilterChange={setNeighborhoodFilter}
                userHistory={userHistory as any}
                onEditUser={handleEditUser}
                onSendEmail={handleOpenUserEmail}
                onReassign={handleReassign}
                onNeighborhoodClick={setNeighborhoodFilter}
              />

            </div>

            {/* Tables */}
            <div
              className="lg:col-span-2"
            >
              <div className="grid md:grid-cols-2 gap-4">
                {filteredTables.map((table) => (
                  <div key={table.id}>
                    <TableCard
                      table={table}
                      cityName={
                        table.restaurantNeighborhoodName
                          ? neighborhoodToCityMap.get(
                              table.restaurantNeighborhoodName.toLowerCase()
                            )
                          : undefined
                      }
                      onLockToggle={handleToggleLock}
                      onEdit={handleEditTable}
                      onEditUser={handleEditUser}
                      onToggleReview={handleToggleReview}
                      onFlagNoShow={dinnerHasHappened ? handleFlagNoShow : undefined}
                      onReassign={handleReassign}
                      onReassignAll={setReassignAllTableId}
                      onSendEmail={handleOpenUserEmail}
                      onNeighborhoodClick={setNeighborhoodFilter}
                      activeUserId={activeId}
                      overElementId={overId}
                      allTables={tables}
                      date={event?.date || ""}
                      userHistory={userHistory as any}
                    />
                  </div>
                ))}
              </div>

              {filteredTables.length === 0 && (
                <div className="card text-center py-16">
                  <Users className="w-16 h-16 text-base-content/40 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">
                    {tables.length === 0 ? "No tables yet" : "No tables found"}
                  </h3>
                  <p className="text-base-content/60 mb-4">
                    {tables.length === 0
                      ? bookingsCount === 0
                        ? "No bookings for this event yet. Tables will be created when bookings are made."
                        : 'Click "Auto-Generate Matches" to create table assignments for the bookings.'
                      : tableSearch
                      ? `No tables match "${tableSearch}"`
                      : "No tables found"}
                  </p>
                  {bookingsCount > 0 && tables.length === 0 && (
                    <button
                      onClick={handleOpenAutoMatchModal}
                      disabled={generateMatches.isPending}
                      className="btn btn-primary mx-auto"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {generateMatches.isPending
                        ? "Generating..."
                        : "Generate Matches"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <DragOverlay dropAnimation={null}>
            {activeId
              ? (() => {
                  const draggedUser =
                    unassignedUsers.find((u) => u.id === activeId) ||
                    tables
                      .flatMap((t) => t.users)
                      .find((u) => u.id === activeId);

                  if (!draggedUser) return null;

                  const quizAnswers = draggedUser.quizAnswers || {};
                  const genderIdentity = quizAnswers.genderIdentity || [];
                  const sexualOrientation = quizAnswers.sexualOrientation || [];
                  const birthday = quizAnswers.birthday;
                  const age = calculateAge(birthday);
                  const genderDisplay = formatArray(genderIdentity);
                  const orientationDisplay = formatArray(sexualOrientation);

                  const history = (userHistory as any)?.[draggedUser.id];
                  const previousRestaurants = history?.previousRestaurants || [];
                  const previousTableMates = history?.previousTableMates || [];

                  return (
                    <div className="bg-dark-elevated p-3 rounded-lg border border-primary-500 hover:border-primary-500 shadow-2xl opacity-90 cursor-grabbing max-w-xs">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {draggedUser.name}
                          </p>
                          <p className="text-xs text-dark-muted truncate">
                            {draggedUser.email}
                          </p>
                        </div>
                        {draggedUser.neighborhood && (
                          <div className="flex items-center gap-1 text-xs text-primary-500 shrink-0 max-w-[8rem]">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="capitalize truncate">
                              {draggedUser.neighborhood}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        {genderDisplay && (
                          <div className="flex items-center gap-1 text-dark-muted">
                            <UserCircle className="w-3 h-3" />
                            <span>{genderDisplay}</span>
                          </div>
                        )}
                        {orientationDisplay && (
                          <div className="flex items-center gap-1 text-dark-muted">
                            <Heart className="w-3 h-3" />
                            <span>{orientationDisplay}</span>
                          </div>
                        )}
                        {age !== null && (
                          <div className="flex items-center gap-1 text-dark-muted">
                            <Cake className="w-3 h-3" />
                            <span>{age}y</span>
                          </div>
                        )}
                      </div>

                      {previousRestaurants.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-base-content/10">
                          <p className="text-[10px] font-semibold opacity-60 mb-1">
                            Previous Restaurants:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {previousRestaurants.map((r: any) => (
                              <span
                                key={r.id}
                                className="text-[10px] bg-base-content/5 px-1.5 py-0.5 rounded text-base-content/80"
                              >
                                {r.name}
                                {r.visitedAt && (
                                  <span className="opacity-60 ml-1">
                                    {new Date(r.visitedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {previousTableMates.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[10px] font-semibold opacity-60 mb-1">
                            Previous Tablemates:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {previousTableMates.map((m: any) => (
                              <span
                                key={m.id}
                                className="text-[12px] bg-primary/5 px-1.5 py-0.5 rounded text-white"
                              >
                                {m.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {draggedUser.score && (
                        <p className="text-xs text-primary-500 mt-2 pt-2 border-t border-base-content/10">
                          Match Score: {draggedUser.score}%
                        </p>
                      )}
                    </div>
                  );
                })()
              : null}
          </DragOverlay>
        </DndContext>

        {/* Table Modal */}
        <TableModal
          isOpen={showTableModal}
          mode={modalMode}
          onClose={() => {
            setShowTableModal(false);
            setEditingTableId(null);
          }}
          onSubmit={handleModalSubmit}
          onDelete={
            editingTableId ? () => handleDeleteTable(editingTableId) : undefined
          }
          restaurants={allRestaurants}
          initialData={
            editingTable
              ? {
                  restaurantId: editingTable.restaurantId || "",
                  capacity: editingTable.capacity,
                  reservationName: editingTable.reservationName,
                  hasUsers: editingTable.users.length > 0,
                }
              : undefined
          }
          isLoading={createTable.isPending || updateTable.isPending}
          isDeleting={deleteTable.isPending}
        />

        {/* Email Modal */}
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEmailResults(null);
          }}
          onSend={handleEmailSend}
          isLoading={sendTableEmails.isPending}
          tables={tableInfoList}
          bulkResults={emailResults?.results}
        />

        {/* Auto Match Modal */}
        <AutoMatchModal
          isOpen={showAutoMatchModal}
          onClose={() => setShowAutoMatchModal(false)}
          onGenerate={handleGenerateMatches}
          isLoading={generateMatches.isPending}
        />

        {/* Match Data Dialog */}
        {showMatchDataDialog && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowMatchDataDialog(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  Auto-Match Data
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowMatchDataDialog(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-8rem)]">
                {generateMatches.isPending ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
                    <span className="ml-3 text-gray-600">Loading match data...</span>
                  </div>
                ) : (
                  matchData.map((data, index) => (
                    <div key={index} className="mb-6 last:mb-0">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                          {data}
                        </pre>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Audit log viewer — scoped to this event. */}
        <AuditLogModal
          isOpen={showAuditLogModal}
          onClose={() => setShowAuditLogModal(false)}
          baseFilters={{ eventTimeId: eventId }}
          contextLabel={
            event?.date
              ? `Event • ${formatDateTimeToDisplay(event.date, event.time)}`
              : "This event"
          }
        />

        {/* Unified reassignment dialog — loads its own data via admin event hooks. */}
        <ReassignmentDialog
          isOpen={showReassignModal && !!reassigningBookingId}
          bookingId={reassigningBookingId}
          eventTimeId={eventId}
          onClose={() => {
            setShowReassignModal(false);
            setReassigningBookingId(null);
          }}
          onSuccess={handleReassignSuccess}
        />

        {/* Batch move/unassign — the reassignment dialog for a whole table. */}
        <TableReassignDialog
          isOpen={!!reassignAllTableId}
          tableId={reassignAllTableId}
          eventTimeId={eventId}
          onClose={() => setReassignAllTableId(null)}
          onSuccess={handleReassignSuccess}
        />

        {/* User Email Modal */}
        {emailingUserId && (() => {
          const targetUser = allUsers.find((u) => u.id === emailingUserId);
          if (!targetUser) return null;
          return (
            <UserEmailModal
              isOpen={showUserEmailModal}
              onClose={() => {
                setShowUserEmailModal(false);
                setEmailingUserId(null);
                setUserEmailDryRunResult(null);
              }}
              onSend={handleUserEmailSend}
              isLoading={sendUserEmails.isPending}
              userName={targetUser.name}
              userEmail={targetUser.email}
              dryRunResult={userEmailDryRunResult}
            />
          );
        })()}

        {/* Edit Reservation Modal — booking-level fields only.
            Table reassignment lives in ReassignmentDialog. */}
        {editingUserId && (() => {
          const editingUser = allUsers.find((u) => u.id === editingUserId);
          const booking = eventData?.data?.bookings?.find((b: any) => b.userId === editingUserId);
          if (!editingUser || !booking) return null;

          return (
            <EditReservationModal
              isOpen={showEditReservationModal}
              onClose={() => {
                setShowEditReservationModal(false);
                setEditingUserId(null);
              }}
              onSubmit={handleEditReservationSubmit}
              eventTimes={(eventsData?.data || [])
                .filter((e: any) => {
                  const eventDateTime = new Date(`${e.date}T${e.time}`);
                  return eventDateTime > new Date();
                })
                .map((e: any) => ({
                  id: e.id,
                  date: e.date,
                  time: e.time,
                }))}
              neighborhoods={(neighborhoodsData?.data?.neighborhoods || []).map((n: any) => ({
                id: n.id,
                name: n.name,
              }))}
              onReassign={() => {
                setReassigningBookingId(booking.id);
                setShowReassignModal(true);
                setShowEditReservationModal(false);
                setEditingUserId(null);
              }}
              initialData={{
                userName: editingUser.name,
                userEmail: editingUser.email,
                eventTimeId: eventId || "",
                neighborhoodId: booking.neighborhoodId || "",
                bookingId: booking.id,
              }}
              isLoading={updateBooking.isPending}
            />
          );
        })()}
      </div>
    </div>
  );
}
